/**
 * Carries the shopper's uploaded design onto a cart-drawer upsell add.
 *
 * The upsell cards ship a "your design here" placeholder in `_design_image`, so an
 * upsell line never showed the rug the shopper just designed. This renders their
 * artwork into each card's own shape and size on an offscreen canvas, uploads that
 * mockup the same way the product page does, and attaches it as `Product preview
 * image` — the property the drawer already renders as a thumbnail with a
 * click-to-zoom modal.
 *
 * All of that happens up front rather than on the tap. The moment the shopper opens
 * the cart drawer, every card on the strip is prepared in the background, one at a
 * time and only on idle, so the uploads never compete with the drawer's own work.
 * Opening the drawer is the trigger on purpose: the strip is not on screen before
 * then, and a page view that never reaches the cart should not be spending renders,
 * uploads and media records on mockups nobody will see. Two things follow:
 *
 * The work splits in two, and only the first half runs ahead of time:
 *
 * The mockup is drawn the way the editor draws: the design contained in the rug,
 * then the shopper's own zoom and rotation applied about its centre, and the whole
 * thing clipped to the shape. Those two numbers ride along onto the new line as
 * `__Scale` and `__Rotation`, so the preview and the order agree.
 *
 *  - Rendering is local and free. A canvas and an object URL are all the strip needs
 *    to stop advertising "your design here" and start showing the shopper's own logo
 *    on that shape, so every card is drawn in the background and swapped in as it
 *    lands. The Liquid placeholder renders until then, and stays put if the draw
 *    fails — the card is never blank and never waits on the network to appear.
 *  - Uploading is deferred to the tap. A mockup only reaches storage, and only earns
 *    a media record, for the rug the shopper actually adds; the two they scrolled
 *    past cost nothing but a canvas. The tap therefore still waits on the upload —
 *    but on the upload alone, with the drawing already done, and a card added a
 *    second time reuses what the first add uploaded.
 *
 * Non-previewable uploads (AI, EPS, anything an <img> cannot decode) have no artwork
 * to draw, so they take the same path the product page takes for them: the file card
 * — extension and filename on a flat tile — is drawn in phase one, uploaded on the
 * tap, and carried on `__Cart image`, exactly as `custom-rug-canvas` writes it for
 * the designed line. The strip keeps its default image for those, since there is no
 * mockup to show.
 *
 * MUST load BEFORE cart-upsell.js. Both register a capture-phase submit listener on
 * document, and capture order is registration order: loading first means a pass-1
 * intercept's stopImmediatePropagation() also suppresses cart-upsell.js's tracking
 * call, so the re-submit in pass 2 reports exactly one "Upsell Add" event instead of
 * two. Where nothing has to be awaited the submit is let through untouched, and the
 * one event fires as usual.
 *
 * Nothing here is required for the add to work. Every failure path falls back to
 * putting the raw upload in `_design_image`, so the shopper sees their own art rather
 * than the placeholder, and the line is added either way.
 */
(function () {
  'use strict';

  var source = null;  // properties of the first line carrying a design

  // variant id -> { start, props, preview, failed }. One record per card, built once
  // per artwork and reused across every drawer re-render.
  var prepared = {};

  // Set the first time the drawer is pulled up, and never cleared: once the shopper
  // has been to the cart, keeping the strip prepared across later cart changes is
  // what makes the next tap instant too.
  var drawerSeen = false;

  // True between a cart change landing and this script knowing what the cart now
  // holds. The drawer re-renders itself from the server in that gap, and painting
  // mockups onto the fresh markup before the re-read is how a design the shopper had
  // just removed came back on the cards — and how an emptied cart reopened still
  // wearing the deleted rug's artwork.
  var stale = false;

  // Identity of the artwork the records above were built from. A cart change that
  // swaps the design — or an editor change to colour or background removal — makes
  // every prepared mockup wrong, and this is what notices.
  var designKey = null;

  function editor() {
    return window.CanvasRugEditor || null;
  }

  function apiBase() {
    var el = customElements.get('custom-rug-canvas');
    return (el && el.API_BASE_URL) || 'https://api.rugtomize.co';
  }

  /* ------------------------------------------------------------------- cart */

  function applyCart(data) {
    source = ((data && data.items) || [])
      .map(function (item) { return item.properties || {}; })
      .find(function (properties) { return properties['__Original image']; }) || null;

    stale = false;
    sync();
  }

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(applyCart)
      .catch(function (error) {
        // Left stale the strip would never paint again, so the old records stand and
        // the next cart event gets another go at reading the truth.
        stale = false;
        console.error('[upsell-design] cart read failed', error);
      });
  }

  /* --------------------------------------------------------------- plumbing */

  function setProp(form, name, value) {
    var selector = 'input[name="properties[' + name + ']"]';
    var input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      form.appendChild(input);
    }
    input.disabled = false;
    input.value = value;
  }

  // "2'x3'", "2’x3’", "2.5'x7'" -> feet. Rectangles and runners are landscape, long
  // side as width, matching how the editor lays a rug out.
  function parseSize(shape, size) {
    var parts = String(size || '')
      .toLowerCase()
      .replace(/×/g, 'x')
      .replace(/[^0-9.x]/g, '')
      .split('x')
      .map(parseFloat);

    var a = parts[0] || 3;
    var b = parts[1] || a;
    if (shape === 'circle') return { w: a, h: a };
    return { w: Math.max(a, b), h: Math.min(a, b) };
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = reject;
      image.src = src;
    });
  }

  // The live editor's foreground is a blob: URL, so it never taints a canvas, and it
  // already reflects a background removal. Preferred over re-fetching the original.
  function designArtUrl() {
    // `__Cart image` is the rendered preview of a non-image upload; `__Original image`
    // on such a line is the raw file, which no <img> can decode.
    return source['__Cart image'] || source['__Original image'];
  }

  function designImage() {
    if (editorIsLive()) return Promise.resolve(editor().fgImg);
    return loadImage(designArtUrl());
  }

  // True when the editor on this page is showing a design we can render from. Every
  // attribute of the mockup is taken from the same place: with the editor live the
  // shopper is looking at it, so it wins over what the cart line recorded earlier.
  // Mixing the two is what produced new artwork in a stale background colour.
  function editorIsLive() {
    var instance = editor();
    return !!(instance && instance.fgImg && instance.fgImg.naturalWidth);
  }

  // No artwork to draw: the upload was a format an <img> cannot decode, so the
  // product page swapped its canvas for a file card. A live canvas settles it on its
  // own; otherwise the designed line says so by carrying its preview on the hidden
  // `__Cart image` rather than the customer-facing `Product preview image`.
  function isFileUpload() {
    if (editorIsLive()) return false;
    var instance = editor();
    if (instance && instance.fileMode) return true;
    return !!(source && !source['Product preview image'] && source['__Cart image']);
  }

  function backgroundColor() {
    if (editorIsLive()) {
      return editor().bgColor || (source && source['__Background Color']) || '#000000';
    }
    return (source && source['__Background Color']) || '#000000';
  }

  function removedBackground() {
    if (editorIsLive()) return editor().shownImage === 'background-removed';
    return source['__Remove Background'] === 'yes';
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  // The zoom and turn the shopper gave their design, as percent and degrees — the
  // same two numbers the editor writes to the line as `__Scale` and `__Rotation`.
  // A non-previewable upload has neither, and gets the identity transform.
  function designTransform() {
    if (editorIsLive()) {
      return {
        scale: toNumber(editor().scalePct, 100),
        rotation: toNumber(editor().rotationDeg, 0)
      };
    }

    return {
      scale: toNumber(source && source['__Scale'], 100),
      rotation: toNumber(source && source['__Rotation'], 0)
    };
  }

  // Everything that changes what a mockup should look like. The pan offset is the one
  // thing left out, because it is not copied onto an upsell rug in the first place,
  // so it has no business busting the cache either.
  function artworkKey() {
    if (!source) return null;
    var art = editorIsLive() ? (editor().fgUrl || 'live') : designArtUrl();
    var transform = designTransform();
    return [
      isFileUpload() ? 'file' : 'image',
      art,
      backgroundColor(),
      removedBackground() ? 'nobg' : 'bg',
      transform.scale,
      transform.rotation
    ].join('|');
  }

  /* ----------------------------------------------------------------- render */

  function renderMockup(shape, size) {
    var W = 600;
    var H = 450;
    var canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;

    var ctx = canvas.getContext('2d');
    var instance = editor();
    var bgSrc = instance && (instance._bgUrl || instance.getAttribute('data-bg-url'));

    function floor() {
      ctx.fillStyle = '#e8e2d6';
      ctx.fillRect(0, 0, W, H);
    }

    // cdn.shopify.com serves Access-Control-Allow-Origin: *, so the tile floor loads
    // CORS-clean and leaves the canvas readable. Anything else just gets the flat fill.
    var background = bgSrc
      ? loadImage(bgSrc).then(function (image) {
          var scale = Math.max(W / image.naturalWidth, H / image.naturalHeight);
          var w = image.naturalWidth * scale;
          var h = image.naturalHeight * scale;
          ctx.drawImage(image, (W - w) / 2, (H - h) / 2, w, h);
        }, floor)
      : Promise.resolve().then(floor);

    return background.then(designImage).then(function (image) {
      var feet = parseSize(shape, size);
      var scale = Math.min(W * 0.72 / feet.w, H * 0.66 / feet.h);
      var w = feet.w * scale;
      var h = feet.h * scale;
      var rx = (W - w) / 2 + 14;
      var ry = (H - h) / 2 + 14;

      function path() {
        ctx.beginPath();
        if (shape === 'circle') {
          ctx.arc(rx + w / 2, ry + h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
        } else {
          ctx.rect(rx, ry, w, h);
        }
      }

      var bgColor = backgroundColor();
      var transform = designTransform();

      // The same three steps the editor's own _drawImageCover takes: contain the
      // design in the rug, apply the shopper's zoom, then turn it about the rug's
      // centre. Matching that fit is what makes their 140% mean 140% here too — a
      // mockup drawn to its own flattering padding would quietly undo the zoom.
      //
      // The clip below stands in for the editor's mask, so a design enlarged or
      // turned past the rug's edge is cut off here exactly as it is there. Only the
      // pan offset is left behind: it is a pixel offset on a canvas sized for the
      // first rug, and means nothing on a shape of different proportions.
      var ratio = image.naturalWidth / image.naturalHeight;
      var dw, dh;
      if (ratio < w / h) {
        dh = h;
        dw = h * ratio;
      } else {
        dw = w;
        dh = w / ratio;
      }
      dw *= transform.scale / 100;
      dh *= transform.scale / 100;

      ctx.save();
      path();
      ctx.clip();
      ctx.fillStyle = bgColor;
      ctx.fillRect(rx, ry, w, h);
      ctx.translate(rx + w / 2, ry + h / 2);
      ctx.rotate(transform.rotation * Math.PI / 180);
      ctx.drawImage(image, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();

      // Outline plus the feet markers the editor draws, so the mockup reads as the
      // same object the shopper was just looking at.
      ctx.save();
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#000';
      path();
      ctx.stroke();

      ctx.fillStyle = '#000';
      ctx.lineWidth = 2;
      ctx.font = "bold 18px system-ui, -apple-system, 'Segoe UI', sans-serif";

      var hy = ry - 10;
      ctx.beginPath();
      ctx.moveTo(rx, hy);
      ctx.lineTo(rx + w, hy);
      ctx.moveTo(rx, hy - 6);
      ctx.lineTo(rx, hy + 6);
      ctx.moveTo(rx + w, hy - 6);
      ctx.lineTo(rx + w, hy + 6);
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.fillText(feet.w + "'", rx + w / 2, hy - 6);

      var vx = rx - 20;
      ctx.beginPath();
      ctx.moveTo(vx, ry);
      ctx.lineTo(vx, ry + h);
      ctx.moveTo(vx - 6, ry);
      ctx.lineTo(vx + 6, ry);
      ctx.moveTo(vx - 6, ry + h);
      ctx.lineTo(vx + 6, ry + h);
      ctx.stroke();
      ctx.textAlign = 'left';
      ctx.fillText(feet.h + "'", vx - 26, ry + h / 2 + 4);
      ctx.restore();

      return new Promise(function (resolve, reject) {
        // Throws SecurityError if anything drawn above tainted the canvas. Rejecting
        // here is what sends the add down the fallback path rather than uploading a
        // zero-byte file.
        try {
          canvas.toBlob(function (blob) {
            blob ? resolve(blob) : reject(new Error('toBlob returned null'));
          }, 'image/webp', 0.8);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  // The name and extension the file card shows. From the live editor when it has
  // one, otherwise from the designed line, which records the name as `Filename`.
  function fileInfo() {
    var instance = editor();
    if (instance && instance.fileInfo && instance.fileInfo.name) return instance.fileInfo;

    var name = (source && source['Filename']) || '';
    return {
      name: name,
      label: name.indexOf('.') > -1 ? name.split('.').pop().toUpperCase() : 'FILE'
    };
  }

  // A copy of `custom-rug-canvas`'s own file card, so an upsell line added from a
  // non-previewable upload shows the drawer exactly what the designed line shows.
  // Nothing external is drawn, so this canvas can never be tainted.
  function renderFileCard() {
    var size = 600;
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    var ctx = canvas.getContext('2d');
    var sansSerif = "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    var info = fileInfo();

    ctx.fillStyle = '#f4f4f4';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.font = 'bold 96px ' + sansSerif;
    ctx.fillText(info.label || 'FILE', size / 2, size / 2);

    // Large and bold because the cart renders this tile at about 96px wide.
    ctx.font = 'bold 64px ' + sansSerif;
    var shown = info.name || '';
    while (shown.length > 4 && ctx.measureText(shown).width > size - 40) {
      shown = shown.slice(0, shown.length - 5) + '…';
    }
    ctx.fillText(shown, size / 2, size / 2 + 90);

    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        blob ? resolve(blob) : reject(new Error('toBlob returned null'));
      }, 'image/png');
    });
  }

  /* ----------------------------------------------------------------- upload */

  // The media record wants the storage path of the original upload, not its signed
  // URL. The live editor still holds that path; the regex is the fallback for a cart
  // the shopper came back to, where only the signed URL survives in the properties.
  function originalPath() {
    var instance = editor();
    var held = instance && instance.uploadedImage && instance.uploadedImage.path;
    if (held) return held;

    var match = /\/object\/sign\/media\/(original\/[^?]+)/.exec(source['__Original image'] || '');
    return match ? decodeURIComponent(match[1]) : null;
  }

  // Store the rendered preview and register it against the original, the same two
  // calls the product page makes. Resolves to what the line item needs to point at it.
  function upload(blob, name, type) {
    var body = new FormData();
    body.append('file', new File([blob], name, { type: type }));

    return fetch(apiBase() + '/api/media/upload-shopify', { method: 'POST', body: body })
      .then(function (response) {
        if (!response.ok) throw new Error('upload-shopify ' + response.status);
        return response.json();
      })
      .then(function (uploaded) {
        var previewPath = uploaded.url;
        return fetch(apiBase() + '/api/media', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            original_path: originalPath(),
            preview_path: previewPath,
            mime_type: source['__File type'] || 'image/png',
            background_color: backgroundColor() || null,
            // Describes the artwork this mockup was actually rendered from. With the
            // editor live that is whatever it is showing — the same signal the product
            // page's own add-to-cart uses — otherwise what the source line recorded.
            remove_bg: removedBackground()
          })
        }).then(function (response) {
          if (!response.ok) throw new Error('media ' + response.status);
          return response.json();
        }).then(function (media) {
          return {
            previewUrl: media.preview_file_url || previewPath,
            mediaId: (media.data && media.data.id) || ''
          };
        });
      });
  }

  /* ---------------------------------------------------------------- prepare */

  // Properties every upsell line carries over from the designed one, whatever shape
  // the artwork ended up being rendered into.
  function sharedProps() {
    var props = {
      '__Original image': source['__Original image'],
      '__Background Color': backgroundColor(),
      '__Remove Background': removedBackground() ? 'yes' : 'no'
    };

    ['Filename', '__File type'].forEach(function (name) {
      if (source[name]) props[name] = source[name];
    });

    // The mockup was drawn with these, so the line has to record them — otherwise the
    // order says 100% and 0° while the preview attached to it is turned and zoomed.
    // A file upload has no transform at all, and the editor leaves both blank there.
    if (!isFileUpload()) {
      var transform = designTransform();
      props['__Scale'] = transform.scale;
      props['__Rotation'] = transform.rotation;
    }

    return props;
  }

  // Phase one, and all of it local: resolves to the blob to upload later, the object
  // URL the card can show in the meantime, and the filename the upload will use.
  // Nothing here touches the network, so it is safe to run for every card on sight.
  function buildArtwork(shape, size) {
    if (isFileUpload()) {
      return renderFileCard().then(function (blob) {
        return {
          blob: blob,
          name: 'preview-image.png',
          type: 'image/png',
          file: true,
          // The strip itself keeps its "your design here" card: a tile reading "AI
          // logo.ai" is the right thumbnail for a cart line and the wrong picture
          // for an advert.
          preview: null
        };
      });
    }

    return renderMockup(shape, size).then(function (blob) {
      return {
        blob: blob,
        name: 'preview-image.webp',
        type: 'image/webp',
        file: false,
        preview: URL.createObjectURL(blob)
      };
    });
  }

  // Phase two's output: what the line item has to carry to point at the stored mockup.
  function propsFor(artwork, stored) {
    var props = sharedProps();
    props['__mediaId'] = stored.mediaId;

    if (artwork.file) {
      // A file-card upload gets no customer-facing preview property, matching how
      // `custom-rug-canvas` writes the designed line: the same URL rides along on
      // the hidden `__Cart image` purely so the drawer has a thumbnail.
      props['__Cart image'] = stored.previewUrl;
      if (source['Note']) props['Note'] = source['Note'];
    } else {
      props['Product preview image'] = stored.previewUrl;
    }

    return props;
  }

  /* ------------------------------------------------------------- scheduling */

  function idle(run) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 200);
    }
  }

  var queue = [];
  var busy = false;

  // One card drawn at a time, each waiting for an idle moment first. Three canvases
  // at once would contend with the drawer's own work on exactly the tap that opened
  // it, which is the lag this whole scheme exists to avoid.
  function pump() {
    if (busy) return;
    var record = queue.shift();
    if (!record) return;

    busy = true;
    idle(function () {
      record.draw().catch(function () {}).then(function () {
        busy = false;
        pump();
      });
    });
  }

  function createRecord(id, shape, size) {
    var record = { preview: null, props: null, failed: false, drawing: null, storing: null };

    // The record may be thrown away mid-draw by a cart change. Anything it produces
    // after that belongs to an artwork the strip has moved on from.
    function current() {
      return prepared[id] === record;
    }

    // Phase one. Normally the queue starts this; a tap on a card that has not come up
    // yet calls it through commit(), which jumps the queue rather than waiting behind
    // the other cards.
    record.draw = function () {
      if (record.drawing) return record.drawing;

      record.drawing = buildArtwork(shape, size).then(function (artwork) {
        if (!current()) {
          if (artwork.preview) URL.revokeObjectURL(artwork.preview);
          return artwork;
        }

        record.preview = artwork.preview;
        showPreviews();
        return artwork;
      }, function (error) {
        console.error('[upsell-design] could not draw ' + shape + ' ' + size, error);
        record.failed = true;
        throw error;
      });

      return record.drawing;
    };

    // Phase two, on the tap only. Memoised on the record, so adding the same card a
    // second time reuses the first add's upload instead of storing it twice.
    record.commit = function () {
      if (record.storing) return record.storing;

      record.storing = record.draw().then(function (artwork) {
        return upload(artwork.blob, artwork.name, artwork.type).then(function (stored) {
          record.props = propsFor(artwork, stored);
          return record.props;
        }, function (error) {
          // A failed upload is almost always the network rather than the artwork, so
          // the attempt is forgotten and a second tap tries again. A failed *draw*
          // is not: record.failed is already set and the card falls back for good.
          record.storing = null;
          throw error;
        });
      });

      return record.storing;
    };

    return record;
  }

  // Put every swapped card back to the image the server rendered. Always run before
  // the object URLs are revoked, so no card is left pointing at a dead blob — and so
  // a design that has left the cart leaves the strip with it.
  function restoreDefaults() {
    document.querySelectorAll('.cart-upsell__card[data-design-preview]').forEach(function (card) {
      var image = card.querySelector('.cart-upsell__img');
      if (image && image.dataset.defaultSrc) image.src = image.dataset.defaultSrc;
      delete card.dataset.designPreview;
    });
  }

  function discardRecords() {
    restoreDefaults();

    Object.keys(prepared).forEach(function (id) {
      if (prepared[id].preview) URL.revokeObjectURL(prepared[id].preview);
      delete prepared[id];
    });
    queue.length = 0;
  }

  // Throw away everything built for an artwork the cart no longer holds. `artworkKey`
  // is null once nothing in the cart carries a design, so emptying the cart discards
  // the records rather than leaving them to be painted back on.
  function refreshRecords() {
    var key = artworkKey();
    if (key === designKey) return;

    discardRecords();
    designKey = key;
  }

  // The record for this card, created on the spot if the background pass never got
  // to it — a tap before the queue came round, or a strip that appeared after the
  // last sync. The tap and the queue go through the same lookup, so a card is never
  // drawn twice.
  function recordForButton(button) {
    var id = button && button.dataset.upsellVariant;
    if (!id) return null;

    if (!prepared[id]) {
      if (designKey === null) designKey = artworkKey();
      prepared[id] = createRecord(
        id,
        (button.dataset.upsellShape || 'rectangle').toLowerCase(),
        button.dataset.upsellSize || ''
      );
    }

    return prepared[id];
  }

  function recordFor(form) {
    return recordForButton(form.querySelector('[data-upsell-variant]'));
  }

  // Queue a record for every card on the strip that does not have one yet. Cheap and
  // idempotent, so it can run on every drawer re-render.
  function prepareAll() {
    if (!drawerSeen || !source) return;

    document.querySelectorAll('.cart-upsell__add[data-upsell-variant]').forEach(function (button) {
      if (prepared[button.dataset.upsellVariant]) return;

      var record = recordForButton(button);
      if (record) queue.push(record);
    });

    pump();
  }

  // Swap in the mockups that are ready. The drawer re-renders from the server on
  // every cart change and brings the placeholder back with it, so this runs again
  // after each one rather than only when a render finishes.
  function showPreviews() {
    document.querySelectorAll('.cart-upsell__card').forEach(function (card) {
      var button = card.querySelector('[data-upsell-variant]');
      var id = button && button.dataset.upsellVariant;
      var record = id ? prepared[id] : null;
      if (!record || !record.preview) return;

      var image = card.querySelector('.cart-upsell__img');
      if (!image || card.dataset.designPreview) return;

      // Decoded before it is swapped in, so the card goes straight from the
      // placeholder to the mockup with no blank frame between the two. The marker
      // also keeps the burst of calls one re-render produces down to a single swap;
      // a re-render replaces the card element, which is what clears it again.
      card.dataset.designPreview = 'loading';

      var loader = new Image();
      loader.onload = function () {
        // A cart change during the decode can discard this record and revoke the URL
        // underneath it, which would leave the card showing a dead blob. Clearing the
        // marker lets whatever replaces it have another go at this card.
        if (prepared[id] !== record || !card.isConnected) {
          delete card.dataset.designPreview;
          return;
        }

        // Kept so the card can go back to it: the strip is re-rendered by the server
        // on most cart changes, but not on all of them.
        image.dataset.defaultSrc = image.src;
        image.removeAttribute('srcset');
        image.src = record.preview;
        card.dataset.designPreview = 'true';
      };
      loader.onerror = function () {
        card.dataset.designPreview = 'failed';
      };
      loader.src = record.preview;
    });
  }

  function sync() {
    // Nothing is painted while the cart is in flux. Whatever the strip is showing is
    // the server's own markup, which is never wrong — only ever less personal.
    if (stale) return;

    refreshRecords();
    prepareAll();
    showPreviews();
  }

  /* -------------------------------------------------------------- intercept */

  function applyProps(form, props) {
    Object.keys(props).forEach(function (name) { setProp(form, name, props[name]); });

    // The placeholder would otherwise win the drawer's thumbnail lookup.
    var placeholder = form.querySelector('input[name="properties[_design_image]"]');
    if (placeholder) placeholder.remove();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('.cart-upsell__form');
    if (!form) return;

    // Pass 2: our own requestSubmit. Let the theme's ProductForm have it.
    if (form.dataset.designReady === 'true') {
      form.dataset.designReady = '';
      return;
    }

    // Nothing designed in the cart, so the placeholder is the honest image.
    if (!source) return;

    var record = recordFor(form);

    // No variant on the card, so there is nothing to draw against. Let the theme add
    // the line with the strip's placeholder on it.
    if (!record) return;

    // Already stored, because this card was added once before. Nothing to wait for:
    // a handful of synchronous DOM writes and the submit carries on untouched, so
    // the theme adds the line and cart-upsell.js still sees its one tracking event.
    if (record.props) {
      applyProps(form, record.props);
      return;
    }

    // The drawing failed, so there is no mockup to store. The shopper's own art beats
    // the placeholder, and waiting would only buy a second failure.
    if (record.failed) {
      setProp(form, '_design_image', designArtUrl());
      return;
    }

    // The ordinary path: the mockup is drawn (or nearly), and this tap is what pays
    // for storing it. Hold the submit while that one round-trip runs.
    event.preventDefault();
    event.stopImmediatePropagation();

    var button = form.querySelector('.cart-upsell__add');
    var label = button ? button.textContent : '';
    if (button) {
      button.dataset.busy = 'true';
      button.textContent = 'Preparing…';
    }

    record.commit()
      .then(function (props) {
        applyProps(form, props);
      })
      .catch(function (error) {
        console.error('[upsell-design] mockup failed, falling back to the shopper\'s own art', error);
        setProp(form, '_design_image', designArtUrl());
      })
      .then(function () {
        if (button) {
          button.textContent = label;
          button.dataset.busy = '';
        }
        form.dataset.designReady = 'true';
        form.requestSubmit(button || undefined);
      });
  }, true);

  /* ------------------------------------------------------------------- wire */

  // Something changed and this script does not yet know what: hold the strip on the
  // server's own markup, then re-read. 300ms behind the event, because the drawer
  // re-renders from the same response and reading /cart.js before that settles
  // returns the pre-change cart.
  function markStale() {
    stale = true;
    setTimeout(readCart, 300);
  }

  // `cart:updated` is the exception: it carries the cart the change returned, which
  // is authoritative and arrives in the same tick as the drawer's re-render. Taking
  // it directly closes the window a removal used to fall into, where the re-render
  // landed first and the strip painted the just-deleted design back onto it.
  document.addEventListener('cart:updated', function (event) {
    var data = event.detail && event.detail.cart;
    if (data && Array.isArray(data.items)) {
      applyCart(data);
      return;
    }
    markStale();
  });

  ['cart:refresh', 'ajaxProduct:added'].forEach(function (name) {
    document.addEventListener(name, markStale);
  });

  // `open` flipping on the drawer is both the signal to start preparing and, on every
  // later open, the cue to put the prepared mockups back on freshly rendered markup.
  // The subtree is watched for the same reason: the drawer re-renders itself from the
  // server on every cart change. Debounced, because one re-render is a burst of
  // mutations.
  var pending = null;
  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    drawerSeen = drawer.hasAttribute('open');

    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) drawerSeen = true;
      clearTimeout(pending);
      pending = setTimeout(sync, 60);
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  readCart();
})();
